import { useEffect, useRef, Ref, useState } from 'react'
import {
  ButtonLink,
  Ribbon,
  PriceLockup,
  Box,
  FlexGrid,
  Card,
  Divider,
  Spacer,
  Callout,
  Typography,
  ChevronLink,
} from '@telus-uds/components-web'
import MemoIcon from '../MemoIcon/MemoIcon'

import { LegalTextRenderer } from '../../../utils/text/TextRenderer'
import {
  StickyCartBlock,
  CartPrice,
  ProductContainer,
  ProductDescription,
  ProductTitle,
  ProductPrice,
  LinkButton,
  StickyCartWrapper,
  IconPosition,
  CartCallout,
  RibbonContainer,
} from './Styles'
import { IStickyCartProductDetails } from './StickyCartTypes'
import ButtonLinkWrapper, { ButtonLinkWrapperProps } from '../ButtonLinkWrapper/ButtonLinkWrapper'

export type CartProps = {
  active?: boolean
  setActive?: (active: boolean) => void
  setCartActive?: (activeState: boolean) => void
  stickyCartRibbon?: string
  stickyCartRibbonWithCoupon?: string
  stickyCartPrice?: string
  stickyCartCouponPrice?: string
  stickyCartPriceSize?: 'small' | 'medium' | 'large'
  stickyCartRateText?: string
  stickyCartPriceSignDirection?: 'left' | 'right'
  strikePrice?: string
  strikeCouponPrice?: string
  strikePriceSize?: 'small' | 'medium' | 'large'
  strikePriceRateText?: string
  strikePriceSignDirection?: 'left' | 'right'
  topStickyCartContract?: string
  bottomStickyCartContract?: string
  stickyCartCallout?: string
  stickyCartPrimaryButtonText?: string
  stickyCartSecondaryButtonText?: string
  stickyCartPrimaryButtonHref?: string
  stickyCartCouponButtonHref?: string
  stickyCartSecondaryButtonHref?: string
  productDetails?: IStickyCartProductDetails[]
  stickyRef?: Ref<HTMLDivElement> | undefined
  stickyCartInMobile?: boolean
  stickyCartInDesktop?: boolean
  stickyCartHeading?: string
  stickyCartSubHeading?: string
  priceToggle?: React.ReactNode | undefined
  priceToggleBlock?
  customButtonLinkCartProps?: ButtonLinkWrapperProps | undefined
  alignStickySecondaryButtonText?: 'left' | 'center' | 'right'
  cartSecondaryButton?: React.ReactNode
  cartSecondaryButtonFields?: boolean
  dropDownYearTerm?: React.ReactNode
  dropDownYearTermBlock?
  primaryButtonHrefThreeYearTermWithCoupon?: string
  primaryButtonHrefThreeYearTermWithoutCoupon?: string
  primaryButtonHrefFiveYearTermWithCoupon?: string
  primaryButtonHrefFiveYearTermWithoutCoupon?: string
}

const Cart = ({
  active,
  setActive,
  setCartActive,
  stickyCartRibbon,
  stickyCartRibbonWithCoupon,
  stickyCartPrice,
  stickyCartPriceSize = 'medium',
  stickyCartCouponPrice,
  stickyCartRateText = 'month',
  stickyCartPriceSignDirection = 'left',
  strikePrice,
  strikeCouponPrice,
  strikePriceSize = 'small',
  strikePriceRateText = '/month',
  strikePriceSignDirection = 'left',
  topStickyCartContract,
  bottomStickyCartContract,
  stickyCartCallout,
  stickyCartPrimaryButtonText,
  stickyCartSecondaryButtonText,
  stickyCartPrimaryButtonHref,
  stickyCartSecondaryButtonHref,
  stickyCartCouponButtonHref,
  productDetails,
  stickyRef,
  stickyCartInMobile,
  stickyCartInDesktop,
  stickyCartHeading,
  stickyCartSubHeading,
  priceToggle,
  priceToggleBlock,
  customButtonLinkCartProps,
  alignStickySecondaryButtonText = 'left',
  cartSecondaryButton,
  cartSecondaryButtonFields = false,
  dropDownYearTerm,
  dropDownYearTermBlock,
  primaryButtonHrefThreeYearTermWithCoupon,
  primaryButtonHrefThreeYearTermWithoutCoupon,
  primaryButtonHrefFiveYearTermWithCoupon,
  primaryButtonHrefFiveYearTermWithoutCoupon,
}: CartProps) => {
  /* 
    useLocalStorage custom hook is used in PriceToggle comp.
    Since the local storage is manipulated inside a useEffect,
    the local storage will be changed asynchronously. onOffState
    stores the initial state read from the local storage,
    but then on each callback from the custom price toggle event,
    this would change synchronously so that the proper elements get
    rendered on the screen. While this is happening, the local storage
    is still changing asyncronously in the background so that the state
    is kept whenever a user reloads a page.
  */
  const onOffState = useRef<boolean>(undefined)
  const [yearTermValue, setYearTermValue] = useState('')
  const [href, setHref] = useState<string>('')
  const yearTermCurrent = useRef<string>(undefined)

  useEffect(() => {
    const currentSessionValue = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
    const yearTerm = localStorage.getItem('yearTerm') === '"5-year"' ? '5-year' : '3-year'
    onOffState.current = currentSessionValue
    yearTermCurrent.current = yearTerm
    setActive(currentSessionValue)
    setYearTermValue(yearTerm)
    setCartActive(currentSessionValue)

    const handleHref = () => {
      if (priceToggleBlock) {
        if (dropDownYearTermBlock) {
          if (yearTermCurrent.current === '5-year') {
            currentSessionValue
              ? setHref(primaryButtonHrefFiveYearTermWithCoupon)
              : setHref(primaryButtonHrefFiveYearTermWithoutCoupon)
          } else if (yearTermCurrent.current === '3-year') {
            currentSessionValue
              ? setHref(primaryButtonHrefThreeYearTermWithCoupon)
              : setHref(primaryButtonHrefThreeYearTermWithoutCoupon)
          }
        } else {
          currentSessionValue ? setHref(stickyCartCouponButtonHref) : setHref(stickyCartPrimaryButtonHref)
        }
      } else {
        setHref(stickyCartPrimaryButtonHref)
      }
    }

    handleHref()

    // TODO: improve coverage and remove the ignore
    /* istanbul ignore next */
    const onLocalChange = (event) => {
      const eventName = event.type
      const currentSessionValueLocal = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
      const yearTermLocal = localStorage.getItem('yearTerm') === '"5-year"' ? '5-year' : '3-year'

      if (priceToggleBlock) {
        if (dropDownYearTermBlock) {
          if (eventName === 'selectDropdownEvent') {
            if (yearTermCurrent.current === '3-year') {
              yearTermCurrent.current = '5-year'
              setYearTermValue(yearTermCurrent.current)
              currentSessionValueLocal
                ? setHref(primaryButtonHrefFiveYearTermWithCoupon)
                : setHref(primaryButtonHrefFiveYearTermWithoutCoupon)
            } else if (yearTermCurrent.current === '5-year') {
              yearTermCurrent.current = '3-year'
              setYearTermValue(yearTermCurrent.current)
              currentSessionValueLocal
                ? setHref(primaryButtonHrefThreeYearTermWithCoupon)
                : setHref(primaryButtonHrefThreeYearTermWithoutCoupon)
            }
          } else {
            onOffState.current = !onOffState.current
            setActive(onOffState.current)
            setCartActive(onOffState.current)
            if (onOffState.current) {
              yearTermLocal === '5-year'
                ? setHref(primaryButtonHrefFiveYearTermWithCoupon)
                : setHref(primaryButtonHrefThreeYearTermWithCoupon)
            } else {
              yearTermLocal === '5-year'
                ? setHref(primaryButtonHrefFiveYearTermWithoutCoupon)
                : setHref(primaryButtonHrefThreeYearTermWithoutCoupon)
            }
          }
        } else {
          onOffState.current = !onOffState.current
          setActive(onOffState.current)
          setCartActive(onOffState.current)
          onOffState.current ? setHref(stickyCartCouponButtonHref) : setHref(stickyCartPrimaryButtonHref)
        }
      }

      if (eventName === 'priceToggleEvent') {
        window.dataLayer.event = {
          name: 'unlock your telus discounts',
          action: onOffState.current ? 'on' : 'off',
          details: window.dataLayer.page.name,
        }
      }
    }

    window.addEventListener('selectDropdownEvent', onLocalChange)
    window.addEventListener('priceToggleEvent', onLocalChange)

    return () => {
      window.removeEventListener('selectDropdownEvent', onLocalChange)
      window.removeEventListener('priceToggleEvent', onLocalChange)
    }
  }, [])

  const secondaryButton = () => {
    return cartSecondaryButtonFields ? (
      cartSecondaryButton
    ) : (
      <ChevronLink href={stickyCartSecondaryButtonHref}>{stickyCartSecondaryButtonText}</ChevronLink>
    )
  }

  let price = ''
  if (priceToggleBlock && stickyCartCouponPrice && active) {
    price = stickyCartCouponPrice
  } else if (stickyCartPrice) {
    price = stickyCartPrice
  }

  return (
    <StickyCartWrapper
      ref={stickyRef}
      stickyCartInMobile={stickyCartInMobile}
      stickyCartInDesktop={stickyCartInDesktop}
      data-testid={'cart-testid'}
    >
      <StickyCartBlock>
        <Card
          variant={{ padding: 'custom' }}
          tokens={{
            paddingBottom: 20,
            paddingLeft: 20,
            paddingRight: 20,
            paddingTop: 20,
          }}
        >
          {stickyCartRibbon && (
            <RibbonContainer>
              <Ribbon left={-8} top={-12} wrap>
                <LegalTextRenderer>
                  {priceToggleBlock && stickyCartRibbonWithCoupon && active
                    ? stickyCartRibbonWithCoupon
                    : stickyCartRibbon}
                </LegalTextRenderer>
              </Ribbon>
            </RibbonContainer>
          )}
          {stickyCartHeading && (
            <div style={{ paddingBottom: '16px' }}>
              <Typography block variant={{ size: 'h3', colour: 'dark' }} heading="h3">
                <LegalTextRenderer>{stickyCartHeading}</LegalTextRenderer>
              </Typography>
            </div>
          )}
          {stickyCartSubHeading && (
            <Typography block variant={{ size: 'h4' }} tokens={{ color: '#414547' }} heading="h3">
              <LegalTextRenderer>{stickyCartSubHeading}</LegalTextRenderer>
            </Typography>
          )}
          <CartPrice>
            <FlexGrid.Row verticalAlign="bottom" horizontalAlign="start">
              <PriceLockup
                price={price}
                size={stickyCartPriceSize}
                rateText={stickyCartRateText}
                signDirection={stickyCartPriceSignDirection}
              />
              {strikePrice && (
                <Box horizontal={2}>
                  <PriceLockup
                    price={priceToggleBlock && strikeCouponPrice && active ? strikeCouponPrice : strikePrice}
                    strikeThrough
                    size={strikePriceSize}
                    a11yText={`was ${strikePrice} dollars per month`}
                    signDirection={strikePriceSignDirection}
                    rateText={strikePriceRateText}
                  />
                </Box>
              )}
            </FlexGrid.Row>
          </CartPrice>
          {topStickyCartContract && (
            <Typography block tokens={{ fontWeight: 300 }}>
              <LegalTextRenderer>{topStickyCartContract}</LegalTextRenderer>
            </Typography>
          )}
          {priceToggleBlock && (
            <>
              {dropDownYearTermBlock && (
                <>
                  <Spacer space={2} /> {dropDownYearTerm}
                </>
              )}
            </>
          )}
          {priceToggleBlock && (
            <>
              <Spacer space={3} />
              {priceToggle}
              <Spacer space={3} />
            </>
          )}
          <Spacer space={2} />
          <Divider />
          <Spacer space={2} />
          {bottomStickyCartContract && (
            <>
              <Typography block tokens={{ fontWeight: 300 }}>
                <LegalTextRenderer>{bottomStickyCartContract}</LegalTextRenderer>
              </Typography>
              <Spacer space={4} />
            </>
          )}
          {productDetails &&
            productDetails.map((product) => {
              return (
                <ProductContainer key={product.productId} data-testid={'stickyCartProduct-testid'}>
                  <ProductDescription color={product.productColor}>
                    <IconPosition>
                      <MemoIcon icon={product.productIcon}></MemoIcon>
                    </IconPosition>
                    <ProductTitle>
                      <LegalTextRenderer>
                        {priceToggleBlock && product.productNameWithCoupon && active
                          ? product.productNameWithCoupon
                          : product.productName}
                      </LegalTextRenderer>
                    </ProductTitle>
                  </ProductDescription>
                  {product.productPrice ? (
                    <ProductPrice>
                      {stickyCartPriceSignDirection === 'left' ? '$' : ''}
                      {priceToggleBlock && product.productCouponPrice && active
                        ? product.productCouponPrice
                        : product.productPrice}
                      {stickyCartPriceSignDirection === 'right' ? '$' : ''}
                    </ProductPrice>
                  ) : (
                    <ProductPrice>
                      {priceToggleBlock && product.productCouponPriceText && active
                        ? product.productCouponPriceText
                        : product.productPriceText}
                    </ProductPrice>
                  )}
                </ProductContainer>
              )
            })}
          {stickyCartCallout && (
            <div style={{ paddingBottom: '16px' }}>
              <Callout variant={{ rounded: true }}>
                <CartCallout>
                  <LegalTextRenderer>{stickyCartCallout}</LegalTextRenderer>
                </CartCallout>
              </Callout>
            </div>
          )}
          <Box top={2} flex={1}>
            {customButtonLinkCartProps ? (
              <ButtonLinkWrapper
                {...customButtonLinkCartProps}
                buttonLinkProps={{
                  ...customButtonLinkCartProps.buttonLinkProps,
                  url: href ?? '',
                }}
              />
            ) : (
              <ButtonLink href={href} variant={{ priority: 'high', width: 'full' }}>
                {stickyCartPrimaryButtonText}
              </ButtonLink>
            )}
            {(cartSecondaryButtonFields || stickyCartSecondaryButtonText) && (
              <LinkButton alignStickySecondaryButtonText={alignStickySecondaryButtonText} data-testid="link-button">
                {secondaryButton()}
              </LinkButton>
            )}
          </Box>
        </Card>
      </StickyCartBlock>
    </StickyCartWrapper>
  )
}

export default Cart
